import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Package } from "lucide-react";
import { formatCurrency } from "@/lib/format";
import { useSettings } from "@/hooks/useSettings";
import { Badge } from "@/components/ui/badge";

const statusColors: Record<string, string> = {
  pending: "bg-warning/15 text-warning-foreground border-warning/30",
  processing: "bg-accent/15 text-accent border-accent/30",
  shipped: "bg-primary/15 text-primary border-primary/30",
  delivered: "bg-success/15 text-success border-success/30",
};

const Orders = () => {
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const settings = useSettings();
  const symbol = settings.currency_symbol || "৳";

  useEffect(() => {
    if (!user) return;
    supabase.from("orders").select("*").eq("user_id", user.id).order("created_at", { ascending: false }).then(({ data }) => {
      setOrders(data ?? []);
      setLoading(false);
    });
  }, [user]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/auth?redirect=/orders" replace />;

  return (
    <div className="container py-12">
      <h1 className="font-display text-3xl md:text-4xl font-bold mb-8">My orders</h1>
      {loading ? (
        <div className="space-y-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-card shadow-soft">
          <Package className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
          <p className="text-muted-foreground mb-4">No orders yet.</p>
          <Button variant="hero" asChild><Link to="/">Start shopping</Link></Button>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <Link
              key={o.id}
              to={`/order-confirmation/${o.id}`}
              className="flex items-center gap-4 p-5 rounded-2xl bg-card shadow-soft hover:shadow-card transition-smooth"
            >
              <div className="grid h-12 w-12 place-items-center rounded-full bg-secondary"><Package className="h-5 w-5" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-medium">Order #{o.id.slice(0, 8)}</p>
                <p className="text-sm text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</p>
              </div>
              <Badge className={statusColors[o.order_status] || ""} variant="outline">{o.order_status}</Badge>
              <span className="font-display font-semibold">{formatCurrency(Number(o.total_amount), symbol)}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Orders;
